import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant,
  DateInput,
  FieldSet,
  Select
} from "shared/components";
import { moneyCentsParser } from "shared/components/forms/BaseInput/parsers";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { usePropertyFormContext } from "../PropertyFormProvider";
import { useAuthContext } from "providers/Auth";
import { statuses } from "shared/app/UnitsTableView/constants";
import { Plus } from "untitledui-js-base";
import { SectionCard } from "shared/app/SectionCard";
import { blankUnit } from "../constants";

export const UnitForms = () => {
  const dictionary = useUnitsPropertiesDictionary();
  const { values, handleChange, errors, setErrors } = usePropertyFormContext();
  const { pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  const updateUnitForm = (
    index: number,
    field: string,
    value: string | number | null | undefined
  ) => {
    handleChange("units", (newValue, prevUnits) => {
      const updatedUnits = [...prevUnits];
      updatedUnits[index] = {
        ...updatedUnits[index],
        [field]: value
      };

      return updatedUnits;
    })("");
    setErrors({ ...errors, [`units[${index}].${field}`]: undefined });
  };

  const removeUnitForm = (index: number) => {
    handleChange("units", (newValue, prevUnits) => {
      return prevUnits.filter((_, i) => i !== index);
    })("");
  };

  const addUnitForm = () => {
    handleChange("units", (newValue, prevUnits) => {
      return [...prevUnits, blankUnit];
    })("");
  };

  return (
    <SectionCard title={dictionary.Units}>
      {values.units.map((unitForm, index) => {
        return (
          <FieldSet
            title={`${dictionary.Unit} ${index + 1}`}
            className="border-none min-w-0"
            key={index + "_unitName"}
          >
            <div className="mb-4 flex gap-4">
              <div className="w-full min-w-0">
                <BaseInput
                  id={`name-${index}`}
                  label={`${dictionary.Unit} name`}
                  placeholder={`${dictionary.Unit} name`}
                  value={unitForm.title}
                  onChange={(value) => updateUnitForm(index, "title", value)}
                  error={errors[`units[${index}].title`]}
                  required
                />
              </div>
              <div className="w-full min-w-0">
                <BaseInput
                  id={`external-id-${index}`}
                  label={`${dictionary.Unit} ID`}
                  placeholder={`${dictionary.Unit} ID`}
                  value={unitForm.external_id || ""}
                  onChange={(value) =>
                    updateUnitForm(index, "external_id", value)
                  }
                  error={errors[`units[${index}].external_id`]}
                />
              </div>
              {homeSiteRentEnabled ? (
                <>
                  <div className="money-input-2 w-full min-w-0">
                    <BaseInput
                      id={`homeRentAmount-${index}`}
                      label="Home rent"
                      placeholder="1,500.00"
                      prefix="$"
                      parser={moneyCentsParser({ max: 9999999 })}
                      value={unitForm.home_rent_amount}
                      onChange={(cents) =>
                        updateUnitForm(index, "home_rent_amount", cents)
                      }
                      required
                      error={errors[`units[${index}].home_rent_amount`]}
                    />
                  </div>
                  <div className="money-input-2 w-full min-w-0">
                    <BaseInput
                      id={`siteRentAmount-${index}`}
                      label="Site rent"
                      placeholder="1,500.00"
                      prefix="$"
                      parser={moneyCentsParser({ max: 9999999 })}
                      value={unitForm.site_rent_amount}
                      onChange={(cents) =>
                        updateUnitForm(index, "site_rent_amount", cents)
                      }
                      error={errors[`units[${index}].site_rent_amount`]}
                      required
                    />
                  </div>
                </>
              ) : (
                <div className="money-input-2 w-full min-w-0">
                  <BaseInput
                    id={`rentAmount-${index}`}
                    label="Rent amount"
                    placeholder="1,500.00"
                    prefix="$"
                    parser={moneyCentsParser({ max: 9999999 })}
                    value={unitForm.monthly_rent_amount}
                    onChange={(cents) =>
                      updateUnitForm(index, "monthly_rent_amount", cents)
                    }
                    required
                    error={errors[`units[${index}].monthly_rent_amount`]}
                  />
                </div>
              )}
            </div>
            <div className="mb-2 flex gap-4">
              <div className="w-full min-w-0">
                <Select
                  label="Current state"
                  id={`occupied-${index}`}
                  value={unitForm.occupied}
                  options={statuses}
                  searchable={false}
                  hideOptional
                  onChange={(e) => {
                    updateUnitForm(index, "occupied", e.target.value);
                  }}
                />
              </div>
              <div className="w-full min-w-0">
                <DateInput
                  id="available_at"
                  label="Available by date"
                  value={unitForm.available_at}
                  readOnly={!unitForm.occupied}
                  hideOptional
                  onChange={(e) => {
                    updateUnitForm(index, "available_at", e.target.value);
                  }}
                />
              </div>
            </div>

            {values.units.length > 1 ? (
              <div className="flex justify-end">
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.LG}
                  className="ml-auto pr-0 text-primary-500 hover:text-primary-700"
                  onClick={() => {
                    removeUnitForm(index);
                  }}
                >
                  Remove
                </Button>
              </div>
            ) : null}
          </FieldSet>
        );
      })}
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.SM}
        onClick={addUnitForm}
        Icon={Plus}
      >
        Add {dictionary.Unit}
      </Button>
    </SectionCard>
  );
};
